/* Native bridge — only active inside the Capacitor wrapper.
 *
 * On the web, every export here is a no-op so the PWA stays the same.
 * Inside the native app, this wires:
 *   - status bar styling
 *   - splash hide
 *   - local notifications scheduling (tomorrow-you letters + gentle nudges)
 *
 * The browser-side fallbacks already exist:
 *   - in-app whisper card for nudges (components/NudgeWhisper)
 *   - silent system notification via the service worker when permission granted
 * This module simply *prefers* OS-scheduled delivery when running natively,
 * because that's the layer that fires while the app is closed.
 */

import { loadPrefs, FREQUENCY_OPTIONS, pickNudge } from "@/lib/nudges";

let _Capacitor = null;
let _LocalNotifications = null;
let _StatusBar = null;
let _SplashScreen = null;
let _initialized = false;

function isNativeAvailable() {
  // window.Capacitor is injected by the native runtime.
  return (
    typeof window !== "undefined" &&
    window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
  );
}

async function load() {
  if (_initialized) return;
  if (!isNativeAvailable()) {
    _initialized = true;
    return;
  }
  try {
    _Capacitor = (await import("@capacitor/core")).Capacitor;
    _LocalNotifications = (await import("@capacitor/local-notifications")).LocalNotifications;
    _StatusBar = (await import("@capacitor/status-bar")).StatusBar;
    _SplashScreen = (await import("@capacitor/splash-screen")).SplashScreen;
  } catch (e) {
    // If imports fail (e.g. running web with no native runtime), stay quiet for the user
    // but make the failure visible to developers.
    console.warn("[native] dynamic plugin import skipped:", e?.message || e);
  }
  _initialized = true;
}

/** Called once on app boot. */
export async function initNative() {
  await load();
  if (!_Capacitor) return;
  try {
    if (_StatusBar) {
      await _StatusBar.setStyle({ style: "DARK" });
      await _StatusBar.setBackgroundColor({ color: "#FBF6EF" });
    }
    if (_SplashScreen) {
      // The config already auto-hides; this is a safety net for slow first paints.
      setTimeout(() => _SplashScreen.hide().catch(() => undefined), 1400);
    }
    // Reschedule the nudge rotation on every cold start so the cadence stays honest.
    await rescheduleNudges();
  } catch (e) {
    // Boot must never disturb the user — log for developer visibility only.
    console.warn("[native] initNative encountered:", e?.message || e);
  }
}

/* -------------------- Permissions -------------------- */

export async function requestNativeNotificationPermission() {
  await load();
  if (!_LocalNotifications) return "unsupported";
  try {
    const status = await _LocalNotifications.requestPermissions();
    return status.display === "granted" ? "granted" : "denied";
  } catch (e) {
    console.warn("[native] notification permission request failed:", e?.message || e);
    return "denied";
  }
}

/* -------------------- Tomorrow-you letters -------------------- */

// Letter notification IDs live in 100000+ space so they never collide with nudges.
function letterNotifId(letterId) {
  // Stable hash to int from string id
  let h = 0;
  for (let i = 0; i < letterId.length; i++) {
    h = (h * 31 + letterId.charCodeAt(i)) | 0;
  }
  return 100000 + Math.abs(h % 800000);
}

/**
 * Schedule a quiet OS notification for when a tomorrow-you letter matures.
 * Body intentionally generic to avoid leaking sensitive content to the OS layer.
 * No-op on web.
 */
export async function scheduleLetterArrival(letter) {
  await load();
  if (!_LocalNotifications) return false;
  const arrives = new Date(letter.arrivesAt);
  if (arrives.getTime() <= Date.now()) return false;
  try {
    await _LocalNotifications.schedule({
      notifications: [
        {
          id: letterNotifId(letter.id),
          title: "UnAnonymous",
          body: "A letter from a past you is waiting.",
          schedule: { at: arrives, allowWhileIdle: true },
          // No sound — quiet by design.
          sound: null,
          smallIcon: "ic_stat_unanonymous",
          iconColor: "#5B3954",
          extra: { kind: "letter", letterId: letter.id },
        },
      ],
    });
    return true;
  } catch (e) {
    console.warn("[native] scheduleLetterArrival failed:", e?.message || e);
    return false;
  }
}

export async function cancelLetterArrival(letterId) {
  await load();
  if (!_LocalNotifications) return;
  try {
    await _LocalNotifications.cancel({
      notifications: [{ id: letterNotifId(letterId) }],
    });
  } catch (e) {
    console.warn("[native] cancelLetterArrival failed:", e?.message || e);
  }
}

/* -------------------- Gentle nudges -------------------- */

// Nudge IDs live in 1..999. We rotate through a small bank.
const NUDGE_ID_BASE = 1;
const NUDGE_BANK_SIZE = 20; // cancel up to this many before rescheduling

function nextNudgeAt(prefs, now = Date.now()) {
  const opt = FREQUENCY_OPTIONS.find((o) => o.id === prefs.frequencyId) || FREQUENCY_OPTIONS[1];
  const perWeek = opt.perWeek;
  // Spacing: 7d / perWeek, with a small jitter to feel human, not robotic.
  const spacingMs = (7 * 24 * 60 * 60 * 1000) / perWeek;
  const jitter = (Math.random() - 0.5) * spacingMs * 0.2;
  return now + spacingMs + jitter;
}

/**
 * Clear any existing scheduled nudges and queue the next N based on prefs.
 * Always fires at 10:00 local of the chosen day so it never wakes the user.
 */
export async function rescheduleNudges() {
  await load();
  if (!_LocalNotifications) return;
  const prefs = loadPrefs();
  try {
    // Cancel any previously scheduled nudge IDs.
    const cancelList = [];
    for (let i = 0; i < NUDGE_BANK_SIZE; i++) {
      cancelList.push({ id: NUDGE_ID_BASE + i });
    }
    await _LocalNotifications.cancel({ notifications: cancelList }).catch(() => undefined);

    if (!prefs.enabled) return;

    // Schedule the next ~2 weeks of nudges, capped.
    const opt = FREQUENCY_OPTIONS.find((o) => o.id === prefs.frequencyId) || FREQUENCY_OPTIONS[1];
    const count = Math.min(NUDGE_BANK_SIZE, opt.perWeek * 2);
    let when = Date.now();
    const schedule = [];
    for (let i = 0; i < count; i++) {
      when = nextNudgeAt({ ...prefs }, when);
      // Pin to 10:00 local on whatever day "when" falls on.
      const d = new Date(when);
      d.setHours(10, 0, 0, 0);
      if (d.getTime() <= Date.now()) {
        d.setDate(d.getDate() + 1);
        when = d.getTime();
      }
      schedule.push({
        id: NUDGE_ID_BASE + i,
        title: "UnAnonymous",
        body: pickNudge(prefs),
        schedule: { at: new Date(when), allowWhileIdle: true },
        sound: null,
        smallIcon: "ic_stat_unanonymous",
        iconColor: "#5B3954",
        extra: { kind: "nudge" },
      });
    }
    if (schedule.length > 0) {
      await _LocalNotifications.schedule({ notifications: schedule });
    }
  } catch (e) {
    console.warn("[native] rescheduleNudges failed:", e?.message || e);
  }
}

export function isNative() {
  return isNativeAvailable();
}
